import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "./ToastProvider";
import cheapestIcon from "../assets/cheapest.svg";
import discountIcon from "../assets/discount.svg";
import saveIcon from "../assets/saveIcon.svg";
import note from "../assets/note.svg";

function formatRelativeUpdated(product) {
  const scrapeTime = product?.scrape_time;
  if (!scrapeTime) return null;
  const now = new Date();
  const today = now.toISOString().split("T")[0];
  const scrapeDate = new Date(`${today}T${scrapeTime}`);
  if (isNaN(scrapeDate.getTime())) return null;
  let diffSec = Math.floor((now - scrapeDate) / 1000);
  if (diffSec < 0) diffSec += 24 * 60 * 60;
  if (diffSec < 45) return "Updated just now";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `Updated ${min} min ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return `Updated ${hr} hr ago`;

  const day = Math.floor(hr / 24);
  return `Updated ${day} day${day === 1 ? "" : "s"} ago`;
}

function ProductCard({
  accent,
  product,
  onClick,
  onAddToList,
  retailerLabel,
  pricingMode = "Base",
  showComparison = true,
}) {
  void accent;
  const [addBusy, setAddBusy] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const productMatchType =
    String(
      product?.response?.match_type ??
        product?.match_type ??
        ""
    ).trim() || "";
  // For listing-style cards (single retailer price), the product itself represents one platform.
  const isProductHigherSizeMatch = productMatchType === "Higher size Match";
  const productNameForTooltip = String(product?.response?.product_name ?? product?.product_name ?? "").trim();

  const normalizePlatformKey = (platform) => {
    const p = String(platform || "").trim().toLowerCase();
    if (p === "morrisons") return "morrison";
    if (p === "sainsburry") return "sainsbury";
    if (p === "sainsburys") return "sainsbury";
    return p;
  };

  const parsePrice = (value) => {
    if (value == null) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, "");
    if (!normalized) return null;
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const platformMeta = {
    tesco: { label: "Tesco", priceColor: "#5B3CC4" },
    asda: { label: "ASDA", priceColor: "#5B3CC4" },
    morrison: { label: "Morrisons", priceColor: "#5B3CC4" },
    sainsbury: { label: "Sainsbury's", priceColor: "#5B3CC4" },
  };

  const knownPlatformKey = normalizePlatformKey(product?.platform);
  const knownStoreMeta = platformMeta[knownPlatformKey] || null;

  const retailerLabelToPlatformKey = (label) => {
    const l = String(label || "").trim().toLowerCase();
    if (l.includes("tesco")) return "tesco";
    if (l.includes("morris")) return "morrison";
    if (l.includes("asda")) return "asda";
    if (l.includes("sainsbury")) return "sainsbury";
    return "";
  };

  const effectivePlatformKey = retailerLabel
    ? retailerLabelToPlatformKey(retailerLabel)
    : knownPlatformKey;

  const comparisonSource =
    Array.isArray(product?.comparison) && product.comparison.length > 0
      ? product.comparison
      : ["tesco", "morrison", "asda", "sainsbury"].map((platform) => ({
          platform,
          price: platform === effectivePlatformKey ? product?.price : null,
        }));

  const comparisonRows = comparisonSource.map((item) => {
    const platform = normalizePlatformKey(
      item?.platform || item?.retailer || item?.provider || item?.store || item?.site
    );
    const meta = platformMeta[platform] || {
      label: item?.platform || item?.retailer || item?.provider || item?.store || "Store",
      priceColor: "#5B3CC4",
    };
    const numericPrice = parsePrice(
      item?.price ?? item?.unit_price ?? item?.price_per_unit ?? item?.offer_price
    );
    const productName =
      String(item?.product_name ?? "").trim() ||
      "";
    const rowMatchType =
      String(item?.match_type ?? "")
        .trim() || "";
    return {
      platform,
      store: meta.label,
      price: Number.isFinite(numericPrice) ? numericPrice : null,
      formatted: Number.isFinite(numericPrice) ? `£${numericPrice.toFixed(2)}` : "N/A",
      priceColor: meta.priceColor,
      match_type: rowMatchType,
      product_name: productName,
    };
  });

  const knownPrices = comparisonRows
    .map((r) => r.price)
    .filter((p) => typeof p === "number" && Number.isFinite(p));

  const cheapestPrice = knownPrices.length ? Math.min(...knownPrices) : null;
  const cheapestRow =
    cheapestPrice == null ? null : comparisonRows.find((r) => r.price === cheapestPrice) || null;

  const highestPrice = knownPrices.length ? Math.max(...knownPrices) : null;
  const potentialSavings =
    cheapestPrice != null && highestPrice != null ? highestPrice - cheapestPrice : null;
  const savings =
    typeof potentialSavings === "number" &&
    Number.isFinite(potentialSavings) &&
    potentialSavings > 0
      ? potentialSavings
      : null;

  const updatedLabel = formatRelativeUpdated(product);
  const categoryLabel = product?.category ? String(product.category) : null;
  const sizeLabel = product?.packsize || product?.weight || null;

  const effectiveRow = comparisonRows.find((r) => r.platform === effectivePlatformKey);
  const basePriceNumber =
    parsePrice(product?.price) ??
    parsePrice(effectiveRow?.price) ??
    null;

  const offerPriceNumber = (() => {
    const offers = Array.isArray(product?.offers) ? product.offers : [];
    const first = offers[0];
    return (
      parsePrice(first?.offer_price) ??
      parsePrice(product?.offer_price) ??
      null
    );
  })();

  const loyaltyPriceNumber =
    parsePrice(product?.loyalty_pricing) ??
    null;

  const chosenPriceNumber = (() => {
    const mode = String(pricingMode || "Base");
    if (mode === "Offer") return offerPriceNumber ?? basePriceNumber;
    if (mode === "Loyalty+Offer") {
      return loyaltyPriceNumber ?? offerPriceNumber ?? basePriceNumber;
    }
    return basePriceNumber;
  })();

  const displayPriceFormatted =
    chosenPriceNumber != null ? `£${chosenPriceNumber.toFixed(2)}` : "£—";

  const hasValidPrice =
    (typeof effectiveRow?.price === "number" && Number.isFinite(effectiveRow.price)) ||
    parsePrice(product?.price) != null;

  const promoText = String(product?.promotion || "").trim();
  const showPromoBanner = !showComparison && promoText.length > 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={hasValidPrice ? onClick : undefined}
      onKeyDown={(e) => {
        if (!hasValidPrice) return;
        if (e.key === "Enter" || e.key === " ") onClick?.();
      }}
      className={[
        "flex h-full min-h-0 flex-col rounded-[18px] border border-[#E6EAF0] bg-white text-left shadow-sm transition-shadow focus:outline-none",
        hasValidPrice ? "hover:shadow-md" : "opacity-70 cursor-not-allowed",
      ].join(" ")}
    >
      <div className="flex min-h-0 flex-1 flex-col p-4">
        {/* Image */}
        <div className="mb-3 flex h-[140px] items-center justify-center rounded-xl bg-[#FFFFFF] sm:h-[200px]">
          {product?.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.title || product.sku}
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl border border-[#E6EAF0] bg-slate-50">
              <svg
                width="34"
                height="34"
                viewBox="0 0 24 24"
                fill="none"
                className="text-slate-400"
                aria-hidden
              >
                <path
                  d="M21 16.2V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v11.2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <path
                  d="M3.5 18.5l4.7-4.7a1.5 1.5 0 0 1 2.1 0l2.4 2.4a1.5 1.5 0 0 0 2.1 0l1.2-1.2a1.5 1.5 0 0 1 2.1 0L21 18.2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.5 8.5h.01"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              <div className="text-center">
                <div className="text-[16px] font-semibold text-slate-600">Image unavailable</div>
                <div className="text-[12px] font-medium text-slate-400">We’ll update it soon</div>
              </div>
            </div>
          )}
        </div>

        <p className="text-[16px] font-semibold text-[#24303A] line-clamp-2 min-h-[36px] sm:text-[18px] sm:min-h-[40px]">
          {product?.title}
        </p>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {categoryLabel && (
              <span className="inline-flex rounded-full bg-[#2EB5C9] px-2.5 py-0.5 text-[12px] font-semibold text-[#FFFFFF]">
                {categoryLabel}
              </span>
            )}
            {sizeLabel && (
              <span className="inline-flex rounded-full bg-[#F59E0B] px-2.5 py-0.5 text-[12px] font-semibold text-[#FFFFFF]">
                {String(sizeLabel)}
              </span>
            )}
          </div>
          {updatedLabel && (
            <span className="shrink-0 text-[12px] font-normal text-[#6E7B86]">{updatedLabel}</span>
          )}
        </div>

        {showComparison ? (
          <>
            <div className="mt-3 rounded-xl border-2 border-[#2FC98D80] bg-[#2FC98D1A] px-3 py-2.5">
              <div className="flex items-start gap-2">
                <img
                  src={cheapestIcon}
                  alt=""
                  width={18}
                  height={18}
                  className="mt-0.5 h-[18px] w-[18px] shrink-0 object-contain"
                  aria-hidden
                />
                <p className="text-[16px] font-semibold leading-snug text-[#24303A]">
                  Cheapest at {cheapestRow?.store || "—"}:{" "}
                  <span
                    className={[
                      "font-semibold",
                      "text-[#24303A]",
                    ].join(" ")}
                  >
                    {cheapestRow?.formatted || "N/A"}
                  </span>
                </p>
              </div>
              {savings != null ? (
                <div className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-rose-600">
                  <img
                    src={saveIcon}
                    alt=""
                    width={18}
                    height={18}
                    className="h-[18px] w-[18px] shrink-0 object-contain"
                    aria-hidden
                  />
                  <span className="text-[#E44A77] font-semibold text-[16px]">
                    Save £{savings.toFixed(2)} vs highest price
                  </span>
                </div>
              ) : (
                <div className="mt-2 h-[18px]" aria-hidden />
              )}
            </div>

            <div className="mt-2 space-y-1.5 text-[15px] sm:text-[16px]">
              {comparisonRows?.map((row) => {
                const isBest =
                  cheapestRow != null && row.platform === cheapestRow.platform && row.price != null;
                const isRowHigherSizeMatch = row?.match_type === "Higher size Match";
                const priceClass = isRowHigherSizeMatch
                  ? "text-[rgb(176,48,28)]"
                  : isBest
                  ? "text-[#2FC98D]"
                  : "text-[#7352C7]";
                return (
                  <div key={row.platform} className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-1.5">          
                        <span
                          className={[
                            "min-w-0 truncate font-normal",
                            isRowHigherSizeMatch ? "text-[rgb(176,48,28)]" : "text-[#24303A]",
                          ].join(" ")}
                        >
                          {row.store}
                        </span>
                        {isBest && (
                          <span className="shrink-0 text-[14px] font-medium text-[#24303A]">
                            (Best)
                          </span>
                        )}
                        {isRowHigherSizeMatch && String(row?.product_name || "").trim() && (
                          <span className="relative group inline-flex shrink-0 text-[rgb(176,48,28)]">
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="9" />
                              <path d="M12 10v6" strokeLinecap="round" />
                              <circle cx="12" cy="7" r="1" fill="currentColor" stroke="none" />
                            </svg>
                            <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-max max-w-[16rem] -translate-x-1/2 rounded-lg border border-[rgb(176,48,28)]/40 bg-white px-2.5 py-1.5 text-[12px] font-medium text-[rgb(176,48,28)] opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                              {row.product_name}
                            </span>
                          </span>
                        )}
                       
                    </span>
                    <span
                      className={["font-semibold", priceClass].join(" ")}
                    >
                      {row.formatted}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {showPromoBanner && (
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/90 px-3 py-1.5">
                <img
                  src={discountIcon}
                  alt=""
                  width={18}
                  height={18}
                  className="h-[18px] w-[18px] shrink-0 object-contain"
                  aria-hidden
                />
                <p className="text-[13px] font-semibold text-[#2FC98D]">
                  {promoText}
                </p>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between gap-3 text-[15px] sm:text-[16px]">
              <span className="flex min-w-0 items-center gap-1.5 text-[15px] sm:text-[16px]">
                <span
                  className={[
                    "min-w-0 truncate font-normal",
                    isProductHigherSizeMatch ? "text-[#2EB5C9]" : "text-[#24303A]",
                  ].join(" ")}
                >
                  {retailerLabel || knownStoreMeta?.label || "Store"}
                </span>
                {isProductHigherSizeMatch && productNameForTooltip && (
                  <span className="relative group inline-flex shrink-0">
                    <img src={note} alt="" className="h-4 w-4" aria-hidden />
                    <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-max max-w-[16rem] -translate-x-1/2 rounded-lg bg-[#24303A] px-2.5 py-1.5 text-[12px] font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                      {productNameForTooltip}
                    </span>
                  </span>
                )}
              </span>
              <span className="flex items-baseline gap-2">
                {basePriceNumber != null &&
                  chosenPriceNumber != null &&
                  chosenPriceNumber !== basePriceNumber && (
                    <span className="text-[13px] font-semibold text-[#6E7B86] line-through">
                      £{basePriceNumber.toFixed(2)}
                    </span>
                  )}
                <span
                  className={[
                    "text-[15px] font-semibold sm:text-[16px]",
                    isProductHigherSizeMatch ? "text-[#2EB5C9]" : "text-[#7352C7]",
                  ].join(" ")}
                >
                  {displayPriceFormatted}
                </span>
              </span>
            </div>
          </>
        )}

        <div className="mt-auto grid grid-cols-2 gap-2 pt-4">
          <button
            type="button"
            disabled={!hasValidPrice}
            className="rounded-full bg-[#7352C71A] px-3 py-1.5 text-[13px] font-bold text-[#7352C7] shadow-sm disabled:cursor-not-allowed disabled:opacity-60 sm:text-[16px]"
            onClick={(e) => {
              e.stopPropagation();
              if (!hasValidPrice) return;
              onClick?.();
            }}
          >
            View details
          </button>
          <button
            type="button"
            disabled={addBusy || !hasValidPrice}
            className="rounded-full bg-[#7352C7] px-3 py-1.5 text-[13px] font-bold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60 sm:text-[16px]"
            onClick={async (e) => {
              e.stopPropagation();
              if (typeof onAddToList !== "function" || addBusy || !hasValidPrice) return;
              setAddBusy(true);
              try {
                await onAddToList(product);
                showToast({
                  type: "success",
                  title: "Added to list",
                  message: "Product added to grocery list.",
                  actionLabel: "Go to Grocery List",
                  onAction: () => {
                    window.location.assign("/settings?tab=grocery&view=lists");
                  },
                });
              } catch (err) {
                const errorMessage = err?.message || "Failed to add product to list.";
                if (errorMessage === "ADD_TO_LIST_CANCELLED" || err?.code === "ADD_TO_LIST_CANCELLED") {
                  return;
                }
                const isNoListError = /No grocery list found/i.test(errorMessage);
                showToast({
                  type: "error",
                  title: "Add failed",
                  message: errorMessage,
                  actionLabel: isNoListError ? "Create list" : "",
                  onAction: isNoListError
                    ? () => navigate("/settings?tab=grocery&view=create")
                    : null,
                });
              } finally {
                setAddBusy(false);
              }
            }}
          >
            {addBusy ? "Adding..." : "Add to list"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductCard;
